package meineglasfaser.frontend.admin.pages.survey

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.role
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.navigation.Anchor
import meineglasfaser.api.SurveyDashboard
import meineglasfaser.api.SurveyStatus
import meineglasfaser.api.routes.AdminRoutes
import meineglasfaser.frontend.common.BackendApi
import meineglasfaser.frontend.common.components.widgets.Modal
import meineglasfaser.frontend.common.components.widgets.ModalCloseBtn
import meineglasfaser.frontend.common.components.widgets.ModalFooterButtons
import meineglasfaser.frontend.common.components.widgets.Toast
import meineglasfaser.frontend.common.components.widgets.Toasts
import meineglasfaser.frontend.common.components.widgets.ariaBusy
import meineglasfaser.frontend.common.components.widgets.rememberQuery
import meineglasfaser.frontend.common.components.yaform.YaForm
import meineglasfaser.frontend.common.components.yaform.YaFormNumberInput
import meineglasfaser.frontend.common.components.yaform.YaFormTextInput
import meineglasfaser.frontend.common.setJsonBody
import meineglasfaser.frontend.common.withAuth
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.dom.Article
import org.jetbrains.compose.web.dom.Br
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Header
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

internal val SurveyStatusDisplayNames = listOf(
    SurveyStatus.IN_PROGRESS to "In Bearbeitung",
    SurveyStatus.COMPLETED to "Fertig",
    SurveyStatus.CANCELLED to "Abgebrochen",
)

@Page
@Composable
fun SurveyDashboardPage(ctx: PageContext) {
    var version by remember { mutableIntStateOf(0) }
    val dashboard = rememberQuery<SurveyDashboard?>(null, version, ctx.route.path) {
        BackendApi.get(AdminRoutes.Survey.Dashboard()) {
            withAuth()
        }
    }
    var showCreateModal by remember { mutableStateOf(false) }

    Div({ classes("flex-row") }){
        H1 { Text("Erhebungen") }
        Div({ classes("flex-grow") })
        Div {
            Button({
                onClick { showCreateModal = true }
            }) {
                Text("Erhebung erstellen")
            }
        }
    }

    if (dashboard == null) {
        Span({ ariaBusy() }) { Text("Dashboard wird geladen...") }
        return
    }

    if (dashboard.numUnassignedEntries > 0) {
        Article({ classes("bg-warn")}) {
            Text("Es gibt ")
            Span({ classes("font-bold") }) { Text("${dashboard.numUnassignedEntries}") }
            Text(" nicht zugewiesene Erhebungseinträge!")
            Br()
            Anchor("/survey/entries") {
                Text("Zu den Erhebungseinträgen")
            }
        }
    }

    Div({ classes("row-fluid")}) {
        dashboard.surveys.forEach { (status, surveys) ->
            Div({ classes("col-12") }) {
                H3 { Text(SurveyStatusDisplayNames.find { it.first == status }?.second ?: status.name) }
            }
            if (surveys.isEmpty()) {
                Div({ classes("col-12") }) {
                    Text("Keine Erhebungen in diesem Status.")
                }
            } else {
                surveys.forEach { survey ->
                    Div({ classes("col-12", "col-md-6", "col-lg-4", "col-xl-3") }) {
                        Article {
                            Header { Text(survey.title) }
                            Div {
                                Text("${survey.numAssignedEntries} / ${survey.households} Haushalte")
                            }
                            Anchor("/survey/entries/${survey.id}", {
                                role("button")
                                classes("outline")
                            }) {
                                Text("Zur Erhebung")
                            }
                        }
                    }
                }
            }
        }
    }

    if (showCreateModal) {
        SurveyCreateModal(onHide = {
            showCreateModal = false
            version++
        })
    }
}

@Composable
private fun SurveyCreateModal(onHide: () -> Unit) {
    Modal(onHide) {
        YaForm(
            onSubmitScope = { data ->
                val body = AdminRoutes.Survey.Create.RequestBody(
                    title = data.string(AdminRoutes.Survey.Create.RequestBody::title),
                    households = data.int(AdminRoutes.Survey.Create.RequestBody::households),
                )
                BackendApi.post(AdminRoutes.Survey.Create()) {
                    withAuth()
                    setJsonBody(body)
                }.fold(
                    onSuccess = {
                        Toasts.display(Toast.success("Erhebung erstellt"))
                        onHide()
                    },
                    onFailure = { error ->
                        Toasts.display(Toast.error("Fehler beim Erstellen der Erhebung: ${error.message}"))
                        console.error("Fehler beim Erstellen der Erhebung", error)
                    }
                )
            }
        ) {
            Header {
                ModalCloseBtn(onHide)
                H3 { Text("Erhebung erstellen") }
            }
            YaFormTextInput(
                prop = AdminRoutes.Survey.Create.RequestBody::title,
                label = { Text("Titel") },
                type = InputType.Text,
                required = true,
            )
            YaFormNumberInput(
                prop = AdminRoutes.Survey.Create.RequestBody::households,
                label = { Text("Anzahl Haushalte") },
                min = 1,
                required = true,
            )
            ModalFooterButtons(onHide, submitLabel = "Erstellen")
        }
    }
}
